import { removeAccents } from "@repo/core/utils/string.utils";

export type RelationshipOption = { value: string; label: string };

const normalizeRelationshipText = (value?: string | null): string =>
  removeAccents(value?.trim().toLowerCase() ?? "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasAnyRelationshipTerm = (value: string, terms: string[]): boolean =>
  terms.some((term) => value.includes(term));

const areRelationshipDescriptionsEquivalent = (
  raw: string,
  optionLabel: string
): boolean => {
  const rawText = normalizeRelationshipText(raw);
  const labelText = normalizeRelationshipText(optionLabel);

  if (!rawText || !labelText) {
    return false;
  }

  const relationshipGroups = [
    ["esposa", "esposo", "marido", "mulher", "conjuge", "companheiro"],
    ["pai", "mae"],
    ["irmao", "irma"],
    ["tio", "tia"],
    ["avo"],
    ["primo", "prima"],
    ["amigo", "amiga"],
    ["colega"],
    ["sogro", "sogra"],
    ["nora", "genro"],
    ["filho", "filha"]
  ];

  return relationshipGroups.some(
    (terms) =>
      hasAnyRelationshipTerm(rawText, terms) &&
      hasAnyRelationshipTerm(labelText, terms)
  );
};

export const matchRelationshipCode = (
  raw: string,
  options: RelationshipOption[]
): string => {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return "";
  }

  const normalizedRaw = removeAccents(trimmed.toLowerCase());
  const byCode = options.find(
    (option) =>
      removeAccents(option.value.trim().toLowerCase()) === normalizedRaw
  );
  if (byCode) {
    return byCode.value;
  }

  const byLabel = options.find(
    (option) =>
      removeAccents(option.label.trim().toLowerCase()) === normalizedRaw
  );
  if (byLabel) {
    return byLabel.value;
  }

  const byEquivalentLabel = options.find((option) =>
    areRelationshipDescriptionsEquivalent(trimmed, option.label)
  );
  return byEquivalentLabel?.value ?? "";
};

export const resolveRelationshipDescription = (
  selectedCode: string,
  options: RelationshipOption[]
): string =>
  options.find((option) => option.value === selectedCode)?.label ??
  selectedCode.trim();
